import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiService, Article, Category } from '../services/api';
import ArticleCard from './ArticleCard';
import colors from '../theme/colors';

const Container = styled.div`
  width: 100%;
  padding: 2rem 0;
`;

const FilterSection = styled.div`
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${colors.mainBlack};
  white-space: nowrap;
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 2px solid ${colors.goldGrey};
  border-radius: 8px;
  background-color: ${colors.mainWhite};
  color: ${colors.mainBlack};
  cursor: pointer;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  min-width: 200px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%231e1e1e' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;

  &:hover {
    border-color: ${colors.mainOrange};
  }

  &:focus {
    outline: none;
    border-color: ${colors.mainOrange};
    box-shadow: 0 0 0 3px rgba(255, 86, 30, 0.1);
  }
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: ${colors.textGray};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${colors.mainOrange};
  background-color: ${colors.goldGrey};
  border: 2px solid ${colors.mainOrange};
  border-radius: 8px;
  margin: 2rem 0;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: ${colors.textGray};
`;

interface ArticlesWithFilterProps {
  showAllOption?: boolean;
}

const ArticlesWithFilter: React.FC<ArticlesWithFilterProps> = ({ showAllOption = true }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Завантажуємо категорії
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiService.getCategories();
        // API функція вже обробляє конвертацію зі Strapi 4 в Strapi 5
        const categoriesData = Array.isArray(response.data)
          ? response.data
          : [];

        setCategories(categoriesData);
      } catch (err: any) {
        setError(`Не вдалося завантажити категорії: ${err.message}`);
      }
    };

    fetchCategories();
  }, []);

  // Завантажуємо статті при зміні вибраної категорії
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        setError(null);

        let articlesData: Article[] = [];

        if (selectedCategory === 'all') {
          // Завантажуємо всі статті
          const response = await apiService.getArticles();
          articlesData = Array.isArray(response.data)
            ? response.data
            : [];
        } else {
          // Завантажуємо статті по категорії
          const response = await apiService.getArticlesByCategory(selectedCategory);
          articlesData = Array.isArray(response.data)
            ? response.data
            : [];
        }

        setArticles(articlesData);
      } catch (err: any) {
        setError(`Не вдалося завантажити статті: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [selectedCategory]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
  };

  if (error && !articles.length) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <FilterSection>
        <FilterLabel htmlFor="category-filter">Фільтр по категоріям:</FilterLabel>
        <Select
          id="category-filter"
          value={selectedCategory}
          onChange={handleCategoryChange}
        >
          {showAllOption && (
            <option value="all">Всі статті</option>
          )}
          {categories.map((category) => (
            <option key={category.id} value={category.slug}>
              {category.name}
            </option>
          ))}
        </Select>
      </FilterSection>

      {loading ? (
        <LoadingMessage>Завантаження статей...</LoadingMessage>
      ) : articles.length === 0 ? (
        <EmptyMessage>
          {selectedCategory === 'all'
            ? 'Статті не знайдено'
            : 'Статті в цій категорії не знайдено'}
        </EmptyMessage>
      ) : (
        <ArticlesGrid>
          {articles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </ArticlesGrid>
      )}
    </Container>
  );
};

export default ArticlesWithFilter;
